<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import { getModule } from 'vuex-module-decorators';
import {
  type UserDto,
  UserService,
  type UserUiDto,
} from '@/api';
import authModule from '@/store/modules/auth';
import store from '@/store';
import SAvatars from '@/components/SAvatars/SAvatars.vue';
import { watch } from 'vue';
import i18n from '@/i18n';

interface UserSelectorProps {
  possibleOwners?: UserDto[];
  name?: boolean;
  multiple?: boolean;
  returnObject?: boolean;
  rules?: any[];
  domainId?: string;
  disabled?: boolean;
}

type UserSelectorEmits = {
  (event: 'input', value: []): void;
}

const auth = getModule(authModule, store);

const props = defineProps({
  possibleOwners: { default: () => [] },
  name: { type: Boolean, default: false },
  multiple: { type: Boolean, default: false },
  returnObject: { type: Boolean, default: false },
  rules: { default: () => [] },
  domainId: null,
  disabled: { type: Boolean, default: false }
});

const emit = defineEmits(["input"]);

const searchString = _ref('');
let loading = _ref(false);
let users = _ref<UserDto[]>([]);

const usersKey = _computed(() => users.value.reduce((acc, user) => `${acc}${user.id}`, ''));
const hint = _computed(() => (props.multiple ? 'Select one or multiple users' : 'Select a user'));
const placeholder = _computed(() => (i18n.tc('owners.placeholder', props.multiple ? 2 : 1)));

const icon = (itemAttrs: any): string => (itemAttrs.inputValue ? 'icon-check-square-fill' : 'icon-check-square-outline-empty');
const color = (itemAttrs: any): string => (itemAttrs.inputValue ? 'iconInfo' : '');
const itemClass = (itemAttrs: any): string => (itemAttrs.inputValue ? 'primary--text v-list-item--active' : '');

const clear = () => {
  if (props.returnObject) {
    emit('input', []);
  }
};

const loadUsers = async () => {
  if (props.disabled) return;

  loading.value = true;

  // If possibleOwners are provided, use them instead of fetched users (already sorted)
  if (props.possibleOwners.length) {
    users.value = props.possibleOwners;
    loading.value = false;
    return;
  }

  // Fetch all users from the service
  const { data = [] } = await UserService.getAllUsers(props.domainId ? { domainId: props.domainId } : {});

  // Map fetched users to the desired format
  users.value = data.map((user: UserUiDto) => ({
    id: user.id,
    name: user.name,
    login: user.email,
  }));

  // Check if auth.getUser exists in the fetched data
  const isAuthUserInList = data.some((user) => user.id === auth.getUser!.id);

  // Add auth.getUser only if they're already in the fetched user list
  if (isAuthUserInList) {
    users.value = [
      auth.getUser!,
      ...data.filter((user) => user.id !== auth.getUser!.id)
        .sort((a: UserUiDto, b: UserUiDto) => a.name.localeCompare(b.name)),
    ];
  } else {
    // Otherwise, just sort the fetched users
    users.value = data.sort((a: UserUiDto, b: UserUiDto) => a.name.localeCompare(b.name));
  }

  loading.value = false;
};

watch(() => props.possibleOwners, () => {
  loadUsers();
}, { deep: true, immediate: true });

watch(() => props.domainId, () => {
  users.value = [];
  loadUsers();
});
</script>

<template lang="pug">
v-autocomplete(
  :key="usersKey"
  class="user-selector-edit-dialog"
  v-bind='$attrs'
  v-on='$listeners'
  :loading="loading"
  :items="users"
  :hint="hint"
  :search-input.sync="searchString"
  :return-object="returnObject"
  @change="searchString=''"
  @click:clear="clear"
  :menu-props="{closeOnClick: true }"
  :placeholder="placeholder"
  :disabled="disabled"
  item-value="id"
  item-text="name"
  data-track="user-selector"
  :rules="rules"
  dense outlined persistent-hint
  clearable
  hide-details
  :multiple="multiple"
  hide-no-data
  cache-items )

  template(v-slot:item="{ item, on, attrs }")
    v-list-item( :class="itemClass(attrs)" ripple @mousedown.prevent v-on="on" active data-track="user-selector-item" )
      v-list-item-action
        v-icon( :color="color(attrs)" ) {{ icon(attrs) }}
      v-list-item-content
        v-list-item-title
          .d-flex.align-center
            SAvatars( :users="[item]" size="20" data-track="user-selector-item-avatar" )

  template(v-slot:selection="{ item }")
    SAvatars.mr-2.my-1( :users="[item]" size="20" data-track="user-selector-input" )

</template>

<style lang="scss" scoped>
.user-selector-edit-dialog {
  .v-select__slot {
    padding: 8px 0px 3px;
  }

  .v-input__append-inner {
    margin-top: 5px !important;
  }
}

.placeholder {
  color: var(--v-textTertiary-base)
}
</style>
